import React, { useState } from 'react';
import { collection, addDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { saveDailyActivity } from '../utils/activityService';
import dailyActivityService from '../services/dailyActivityService';

const LearningSpaceShareModal = ({ 
  isOpen, 
  onClose, 
  activityId, 
  imageUrl, 
  activityData,
  onShareComplete,
  activityPageUrl // Add this prop to get the activity page URL
}) => {
  const { user } = useAuth();
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');

  const handleShareDecision = async (shouldShare) => {
    if (!user || !activityId || !imageUrl) return;

    try {
      setSharing(true);
      setError('');

      // NOW create the submission and mark activity as complete
      // This is where the submission should be registered, not in "Mark Complete"
      
      // 1. Create activity participant record FIRST (this unlocks the public wall and is required for read access)
      const participantId = `${activityId}_${user.uid}`;
      try {
        await setDoc(doc(db, 'activityParticipants', participantId), {
          activityId: activityId.toString(),
          uid: user.uid,
          completed: true,
          createdAt: serverTimestamp()
        });
        console.log('✅ Activity participant created:', participantId);
      } catch (participantError) {
        // If participant already exists, that's okay - continue
        if (participantError.code !== 'permission-denied') {
          console.warn('⚠️ Error creating participant (may already exist):', participantError.message);
        }
      }
      
      // 2. Create submission document in the submissions collection
      const submissionData = {
        activityId: activityId.toString(), // Ensure it's a string
        uid: user.uid,
        imageUrl: imageUrl,
        // Denormalized artist display fields for ActivityWallModal/PublicWallCarousel
        artistName: user.displayName || user.email || '',
        artistPhotoURL: user.photoURL || '',
        artistType: user.accountType || 'student',
        isPublic: shouldShare,
        likesCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const submissionRef = await addDoc(collection(db, 'submissions'), submissionData);
      console.log('✅ Submission created:', submissionRef.id);

      // 3. Save activity to userActivities collection (mark as completed)
      await saveDailyActivity({
        activityId: activityId,
        activityTitle: activityData?.title || `Daily Art Activity ${activityId}`,
        activityDescription: activityData?.description || "Daily creative activity for developing artistic thinking",
        userArtworkUrl: imageUrl,
        activityPageUrl: activityPageUrl || '',
        isCompleted: true,
        completedAt: new Date().toISOString(),
        metadata: {
          prerequisites: activityData?.prerequisites || "Art supplies and imagination",
          steps: activityData?.steps || "Follow the activity instructions and create your artwork",
          category: activityData?.category || "Creative Expression",
          difficulty: activityData?.difficulty || "Beginner"
        }
      });
      console.log('✅ Activity saved to userActivities collection');

      // 4. Mark as completed in user progress via backend
      try {
        await dailyActivityService.completeActivity(user.uid, activityId);
        console.log('✅ Activity marked as complete in user progress');
      } catch (progressError) {
        console.warn('⚠️ Could not update user progress via backend:', progressError);
        // Still continue even if progress update fails
      }

      console.log('✅ Learning Space integration completed:', {
        submissionId: submissionRef.id,
        isPublic: shouldShare,
        participantId
      });

      // Call completion callback
      if (onShareComplete) {
        onShareComplete(shouldShare, submissionRef.id);
      }

      onClose();

    } catch (error) {
      console.error('❌ Error sharing to Learning Space:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      
      // Provide more specific error message
      if (error.code === 'permission-denied') {
        setError('Permission denied. Please make sure you are logged in and try again.');
      } else {
        setError(`Failed to share to Learning Space: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setSharing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-amber-800 mb-4">
            Share on the Artmosphere Museum?
          </h3>
          <p className="text-amber-700 mb-6">
            Would you like to share your artwork on the Artmosphere Museum so other young artists
            can see your creation? You can keep it private if you prefer.
          </p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => handleShareDecision(false)}
              disabled={sharing}
              className="bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-semibold py-3 px-6 rounded-full transition-colors"
            >
              No, keep private
            </button>
            <button
              onClick={() => handleShareDecision(true)}
              disabled={sharing}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-amber-300 disabled:to-orange-300 text-white font-semibold py-3 px-6 rounded-full transition-colors"
            >
              {sharing ? 'Sharing...' : 'Yes, share on Museum'}
            </button>
          </div>
          
          <p className="text-xs text-amber-600 mt-4">
            You can change this setting later in your activity history.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LearningSpaceShareModal;
